import type { ReactNode } from "react";
import type { SeedingEntry } from "../lib/types.js";
import { Avatar } from "./Avatar.js";

/** Two decimals, as the spec words it: a mean of 1.667 reads 1.67. */
export function formatMean(mean: number): string {
  return mean.toFixed(2);
}

/**
 * What the panel agrees on, as a ruled list in the final order. Used for the live average an
 * organiser reads while seeding is open and for the one fixed when it is closed, so the two
 * cannot drift into saying different things.
 *
 * The best and worst place sit beside the mean because a mean alone cannot tell a player
 * everybody put fifth from one half the panel put first and half put ninth.
 */
export function SeedingAverage({ entries }: { entries: SeedingEntry[] }) {
  if (entries.length === 0) return null;

  const { lists, panel } = entries[0]!;
  const ranked = entries.filter((entry) => entry.mean !== null);
  const unranked = entries.filter((entry) => entry.mean === null);

  return (
    <div>
      <div
        aria-hidden
        className="font-mono mt-3 grid grid-cols-[1.75rem_minmax(0,1fr)_3.5rem_4rem] items-center gap-x-3 text-[11px] uppercase tracking-[0.06em] text-ink-muted"
      >
        <span>#</span>
        <span>Player</span>
        <span className="text-right">Mean</span>
        <span className="text-right">Range</span>
      </div>
      <ol>
        {ranked.map((entry) => (
          <Row key={entry.memberId} entry={entry}>
            {/* Fewer lists ranked this player than were counted: the rest skipped them. */}
            {entry.ranked < entry.lists ? `by ${entry.ranked} of ${entry.lists}` : null}
          </Row>
        ))}
      </ol>
      {unranked.length > 0 ? (
        <ul className="mt-3 border-t-2" style={{ borderColor: "var(--color-line-muted)" }}>
          {unranked.map((entry) => (
            <Row key={entry.memberId} entry={entry}>
              Not ranked by the panel
            </Row>
          ))}
        </ul>
      ) : null}
      <p className="font-mono mt-2 text-[11px] text-ink-muted">
        {lists} of {panel} ranked
      </p>
    </div>
  );
}

/**
 * One player. A player nobody ranked has no place, mean or range, and says so underneath; one
 * some lists skipped says how many ranked them.
 */
function Row({ entry, children }: { entry: SeedingEntry; children: ReactNode }) {
  const none = entry.mean === null;
  return (
    <li
      className="grid min-h-[48px] grid-cols-[1.75rem_minmax(0,1fr)_3.5rem_4rem] items-center gap-x-3 border-b py-1.5"
      style={{ borderColor: "var(--color-line-muted)" }}
    >
      <span className="font-mono text-[13px] font-semibold">{none ? "–" : entry.order}</span>
      <span className="flex min-w-0 items-center gap-2">
        <Avatar name={entry.displayName} url={entry.avatarUrl} size={28} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] font-semibold">{entry.displayName}</span>
          {children ? (
            <span className="font-mono truncate text-[11px] text-ink-muted">{children}</span>
          ) : null}
        </span>
      </span>
      <span className="font-mono text-right text-[13px] font-semibold">
        {entry.mean === null ? "–" : formatMean(entry.mean)}
      </span>
      <span className="font-mono text-right text-[11px] text-ink-muted">
        {entry.best === null || entry.worst === null
          ? "–"
          : entry.best === entry.worst
            ? entry.best
            : `${entry.best}–${entry.worst}`}
      </span>
    </li>
  );
}
